import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/lib/auth";


import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Onboarding from "@/pages/Onboarding";
import TrialExpired from "@/pages/TrialExpired";
import PublicInvoice from "@/pages/PublicInvoice";

import CompanyLayout from "@/components/CompanyLayout";
import SuperLayout from "@/components/SuperLayout";

import Dashboard from "@/pages/Dashboard";
import Bookings from "@/pages/Bookings";
import BookingsCalendar from "@/pages/BookingsCalendar";
import BookingCreate from "@/pages/BookingCreate";
import BookingDetail from "@/pages/BookingDetail";
import Clients from "@/pages/Clients";
import Invoices from "@/pages/Invoices";
import InvoiceCreate from "@/pages/InvoiceCreate";
import InvoiceDetail from "@/pages/InvoiceDetail";
import EventTypes from "@/pages/EventTypes";
import CustomFields from "@/pages/CustomFields";
import Team from "@/pages/Team";
import Settings from "@/pages/Settings";
import ActivityLog from "@/pages/ActivityLog";
import Profile from "@/pages/Profile";
import Subscription from "@/pages/Subscription";

import SuperDashboard from "@/pages/super/SuperDashboard";
import SuperCompanies from "@/pages/super/SuperCompanies";
import SuperCompanyTypes from "@/pages/super/SuperCompanyTypes";
import SuperLoginAttempts from "@/pages/super/SuperLoginAttempts";
import SuperSettings from "@/pages/super/SuperSettings";
import SuperPlans from "@/pages/super/SuperPlans";
import SuperSubscriptions from "@/pages/super/SuperSubscriptions";
import SuperSubscriptionInvoices from "@/pages/super/SuperSubscriptionInvoices";

function FullPageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-sm text-muted-foreground">Loading…</div>
    </div>
  );
}

function isSubscriptionBlocked(status) {
  const normalized = String(status || "").trim().toLowerCase();
  return normalized === "expired" || normalized === "cancelled";
}

function ProtectedRoute({ children, roles }) {
  const { user } = useAuth();
  if (user === null) return <FullPageLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/app" replace />;
  return children;
}

function TenantGate({ children }) {
  const { user, company } = useAuth();
  const location = useLocation();
  if (user === null) return <FullPageLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (!["admin", "manager", "employee", "viewer"].includes(user.role)) return <Navigate to="/" replace />;
  if (company && isSubscriptionBlocked(company.subscription_status) && !location.pathname.startsWith("/app/subscription")) {
    return <Navigate to="/app/subscription" replace />;
  }
  return children;
}

function RootRedirect() {
  const { user, company } = useAuth();
  const location = useLocation();
  if (user === null) return <FullPageLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "super_admin") return <Navigate to="/super" replace />;
  if (company && !company.onboarded) return <Navigate to="/onboarding" replace />;
  if (company && isSubscriptionBlocked(company.subscription_status) && !location.pathname.startsWith("/app/subscription")) {
    return <Navigate to="/app/subscription" replace />;
  }
  return <Navigate to="/app/bookings/calendar" replace />;
}

function AppShell() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<RootRedirect />} />
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/invoice/public/:token" element={<PublicInvoice />} />
        <Route
          path="/onboarding"
          element={
            <ProtectedRoute>
              <Onboarding />
            </ProtectedRoute>
          }
        />
        <Route
          path="/trial-expired"
          element={
            <ProtectedRoute>
              <TrialExpired />
            </ProtectedRoute>
          }
        />

        {/* Company workspace */}
        <Route
          path="/app"
          element={
            <TenantGate>
              <CompanyLayout />
            </TenantGate>
          }
        >
          <Route index element={<Navigate to="/app/bookings/calendar" replace />} />
          <Route path="bookings" element={<Bookings />} />
          <Route path="bookings/calendar" element={<BookingsCalendar />} />
          <Route path="bookings/new" element={<BookingCreate />} />
          <Route path="bookings/:id" element={<BookingDetail />} />
          <Route path="clients" element={<Clients />} />
          <Route path="invoices" element={<Invoices />} />
          <Route path="invoices/new" element={<InvoiceCreate />} />
          <Route path="invoices/:id" element={<InvoiceDetail />} />
          <Route path="event-types" element={<EventTypes />} />
          <Route path="custom-fields" element={<CustomFields />} />
          <Route path="team" element={<Team />} />
          <Route path="settings" element={<Settings />} />
          <Route path="activity" element={<ActivityLog />} />
          <Route path="profile" element={<Profile />} />
          <Route path="subscription" element={<Subscription />} />
        </Route>

        {/* Super admin workspace */}
        <Route
          path="/super"
          element={
            <ProtectedRoute roles={["super_admin"]}>
              <SuperLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<SuperDashboard />} />
          <Route path="companies" element={<SuperCompanies />} />
          <Route path="company-types" element={<SuperCompanyTypes />} />
          <Route path="login-attempts" element={<SuperLoginAttempts />} />
          <Route path="plans" element={<SuperPlans />} />
          <Route path="subscriptions" element={<SuperSubscriptions />} />
          <Route path="subscription-invoices" element={<SuperSubscriptionInvoices />} />
          <Route path="settings" element={<SuperSettings />} />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <AppShell />
      <Toaster position="top-right" richColors />
    </AuthProvider>
  );
}
